import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SbAnimatedNumberComponent } from '../sb-animated-number/sb-animated-number.component';
import { IconsModule } from '../../icons/icons.module';
import { Currency } from '../currency-selector/currency-country-codes';
import { combineLatest, of, ReplaySubject, Subject } from 'rxjs';
import { DecimalFormatStyleType } from '@phensley/cldr-core/lib/common/numbers';
import { CurrencyFormatStyleType } from '@phensley/cldr';
import { SbTooltipInfoComponent } from '../sb-tooltip-info/sb-tooltip-info.component';
import { fadeInOut } from '../../animations/fade-in-out.animation';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    shareReplay,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    PdfReportItem,
    PdfReportItemInput
} from '../../../features/pdf-report/state/pdf-report.model';
import { PdfReportService } from 'src/app/features/pdf-report/state/pdf-report.service';
import { PdfReportQuery } from 'src/app/features/pdf-report/state/pdf-report.query';

export type SbKeyMetrics = Array<SbKeyMetric>;
export interface SbKeyMetric {
    /** <b>id</b> - id for metric. <p>**Default:** `null`</p> */
    id?: string;
    /** <b>title</b> - title for metric. <p>**Default:** `null`</p> */
    title: string;
    /** <b>description</b> - description for metric. TODO: implement <p>**Default:** `null`</p> */
    description?: string;
    /** <b>value</b> - numeric value for metric. <p>**Default:** `null`</p> */
    value: number | string;
    /** <b>change</b> - value in percent for change, e.g. `0.13`. <p>**Default:** `null`</p> */
    change?: number | [number, number];
    /** <b>prefixPositiveValue</b> - add a `+` sign in front of positive values. <p>**Default:** `false`</p> */
    prefixPositiveValue?: boolean;
    /** <b>ccy</b> - currency to use for metric. <p>**Default:** `null`</p> */
    ccy?: Currency;
    /** <b>prefix</b> - prefix to place in front of number. <p>**Default:** `null`</p> */
    prefix?: string;
    /** <b>suffix</b> - suffix to place after number. <p>**Default:** `null`</p> */
    suffix?: string;
    /** <b>addColor</b> - add color to negative and positive values. <p>**Default:** `false`</p> */
    addColor?: boolean;
    /** <b>formatStyle</b> - format style to apply to value when displayed. <p>**Default:** `decimal` and `code` if currency is passed.</p> */
    formatStyle?: DecimalFormatStyleType | CurrencyFormatStyleType;
    /** <b>minimumFractionDigits</b> - minimum number of decimals to show, 0 equals none. <p>**Default:** `value from settings`</p> */
    minimumFractionDigits?: number;
    /** <b>maximumFractionDigits</b> - maximum number of decimals to show, 0 equals none. Number will be rounded accordingly. <p>**Default:** `value from settings`</p> */
    maximumFractionDigits?: number;
    /** <b>type</b> - type of value to display. <p>**Default:** `number`</p> */
    type?: 'number' | 'text';
    /** <b>loading</b> - indicates if the metric is loading. <p>**Default:** `false`</p> */
    loading?: boolean;
}
@Component({
    selector: 'sb-key-metrics',
    standalone: true,
    imports: [
        CommonModule,
        SbAnimatedNumberComponent,
        IconsModule,
        SbTooltipInfoComponent
    ],
    templateUrl: './sb-key-metrics.component.html',
    styleUrls: ['./sb-key-metrics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class SbKeyMetricsComponent implements OnInit, OnDestroy {
    private _unsubscribe$ = new Subject();
    get forceHorizontal(): boolean {
        return this._forceHorizontal;
    }

    @Input() set forceHorizontal(value: boolean) {
        this._forceHorizontal = value;
    }

    @Input() set metrics(metrics: SbKeyMetrics) {
        this._metrics$.next(metrics);
        // store a static copy of the metrics (for the pdf export)
        this._metrics = metrics;
    }

    private _reportConfig$ = new ReplaySubject<{
        heading: string;
        order: number;
        description: string;
        pageBreak?: 'before' | 'after';
    }>(1);

    reportConfig$ = this._reportConfig$.asObservable();

    @Input() set reportConfig(value: typeof this._reportConfig) {
        this._reportConfig$.next(value);
    }

    private _metrics$ = new ReplaySubject<SbKeyMetrics>(1);

    get metrics$() {
        return this._metrics$.asObservable().pipe(shareReplay(1));
    }
    private _forceHorizontal: boolean = false;
    private _metrics: SbKeyMetrics;

    private _reportItem: PdfReportItem;
    private _reportConfig: {
        heading: string;
        order: number;
        description: string;
        pageBreak?: 'before' | 'after';
    };

    constructor(
        private _pdfReportService: PdfReportService,
        private _pdfReportQuery: PdfReportQuery
    ) {}

    trackByIndexFn(index: number) {
        return index;
    }

    ngOnDestroy() {
        this._unsubscribe$.next(true);
        this._unsubscribe$.complete();
        // if there is a report item, and it has an id...
        if (this._reportItem && this._reportItem.id) {
            // ...remove the report item
            this._pdfReportService.removePdfReportItems(this._reportItem.id);
        }
    }

    ngOnInit() {
        // when the export modal is active/open...
        combineLatest([
            this._pdfReportQuery.exportModalIsActive$.pipe(
                filter((active) => active),
                distinctUntilChanged()
            ),
            this.reportConfig$,
            this._metrics$.pipe(debounceTime(300))
        ])
            .pipe(
                tap(([_, reportConfig, metrics]) => {
                    // ...if there is a report config
                    if (reportConfig) {
                        if (this._reportItem && this._reportItem.id) {
                            // ...remove the report item
                            this._pdfReportService.removePdfReportItems(
                                this._reportItem.id
                            );
                        }
                        // ...register the report item
                        const reportItemInput: PdfReportItemInput = {
                            name: reportConfig?.heading,
                            description: reportConfig?.description,
                            type: 'keyMetrics',
                            order: reportConfig?.order,

                            keyMetrics: {
                                data$: of(this._metrics), // we pass a cold observable with a copy of the latest metrics
                                heading: reportConfig?.heading
                            },
                            // optionally add page break if truthy
                            ...(reportConfig?.pageBreak
                                ? { pageBreak: reportConfig?.pageBreak }
                                : {})
                        };
                        // we store the report item in a variable to be able to remove it when the component is destroyed
                        this._reportItem =
                            this._pdfReportService.registerPdfReportItems(
                                reportItemInput
                            );
                    }
                }),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();
    }

    protected readonly isNaN = isNaN;
}
